import React from 'react';
import { Link } from 'react-router-dom';
import Navbar from './Navbar';
import SEO from './SEO';
import Transition from './Transition';
import ContactSection from './ContactSection';
import { insights } from '../data/insights';
import useReveal from '../hooks/useReveal';

const INSIGHTS_DESCRIPTION =
  'Notes on branding, motion design and web/UX from Sander Kuyken — practical thinking on how design decisions help small businesses and products look and perform better.';

const INSIGHTS_JSONLD = {
  '@context': 'https://schema.org',
  '@type': 'CollectionPage',
  name: 'Insights',
  description: INSIGHTS_DESCRIPTION,
  url: 'https://portfolio.skdesign.be/insights',
  hasPart: insights.map((entry) => ({
    '@type': 'Article',
    headline: entry.title,
    description: entry.description,
    datePublished: entry.date,
    url: `https://portfolio.skdesign.be/insights/${entry.slug}`,
  })),
};

const InsightsPage = () => {
  const articles = [...insights].sort((a, b) => (a.date < b.date ? 1 : -1));
  const listRef = useReveal({ threshold: 0.12, stagger: 110 });

  return (
    <>
      <SEO
        title="Insights"
        description={INSIGHTS_DESCRIPTION}
        path="/insights"
        jsonLd={INSIGHTS_JSONLD}
      />
      <Navbar />

      <main className="bg-[#f2f3d9]">
        {/* Hero */}
        <section className="pt-28 sm:pt-32 lg:pt-40 pb-12 sm:pb-16 lg:pb-20">
          <div className="max-w-[1422px] mx-auto px-5 sm:px-6 lg:px-8">
            <div className="flex flex-col gap-8 sm:gap-10 lg:gap-14">
              <h1 className="font-anton text-mainkleur text-[18vw] sm:text-[140px] lg:text-[220px] leading-[0.88] tracking-[-4px] sm:tracking-[-6px] lg:tracking-[-9px] uppercase">
                Insights
              </h1>
              <div className="flex flex-col lg:flex-row gap-8 lg:gap-16 lg:items-end justify-between">
                <p className="font-space text-mainkleur text-lg sm:text-xl lg:text-[22px] leading-[1.4] max-w-[680px]">
                  Short, practical notes on branding, motion and web/UX design — the thinking behind the work, written for people running real businesses and products.
                </p>
                <span className="font-space text-mainkleur/60 text-xs sm:text-sm uppercase tracking-[0.22em] whitespace-nowrap">
                  {articles.length} {articles.length === 1 ? 'article' : 'articles'}
                </span>
              </div>
              <div className="w-full h-px bg-mainkleur/30" />
            </div>
          </div>
        </section>

        {/* Article list */}
        <section className="pb-20 sm:pb-28 lg:pb-32">
          <div className="max-w-[1422px] mx-auto px-5 sm:px-6 lg:px-8">
            <div ref={listRef} className="flex flex-col">
              {articles.map((entry, i) => (
                <Link
                  key={entry.slug}
                  to={`/insights/${entry.slug}`}
                  className="reveal-up group grid grid-cols-1 lg:grid-cols-12 gap-5 lg:gap-12 items-center py-8 sm:py-10 border-t border-mainkleur/15 first:border-t-0 hover:bg-mainkleur/[0.03] transition-colors duration-300 rounded-lg lg:px-4"
                >
                  {entry.cover && (
                    <div className="lg:col-span-4 overflow-hidden rounded-xl bg-mainkleur/5 aspect-[16/10]">
                      <img
                        src={entry.cover}
                        alt=""
                        loading="lazy"
                        className="w-full h-full object-cover transition-transform duration-500 ease-out group-hover:scale-[1.05]"
                      />
                    </div>
                  )}
                  <div className={entry.cover ? 'lg:col-span-8' : 'lg:col-span-12'}>
                    <div className="flex items-center gap-3 mb-3">
                      <span className="font-space text-mainkleur/55 text-xs uppercase tracking-[0.22em]">
                        {String(i + 1).padStart(2, '0')}
                      </span>
                      <span className="inline-flex w-max items-center bg-mainkleur/10 text-mainkleur border border-mainkleur/20 rounded-full px-3 py-1 text-xs font-space font-semibold uppercase tracking-[0.12em]">
                        {entry.tag}
                      </span>
                    </div>
                    <h2 className="font-bricolage font-extrabold text-mainkleur text-[28px] sm:text-[36px] lg:text-[44px] leading-[0.95] tracking-tight uppercase group-hover:text-verloop-oranje-mid transition-colors duration-300">
                      {entry.title}
                    </h2>
                    <p className="mt-3 font-space text-mainkleur/80 text-base sm:text-lg leading-[1.5] max-w-[760px]">
                      {entry.description}
                    </p>
                    <div className="mt-4 flex items-center gap-4 font-space text-mainkleur/55 text-xs sm:text-sm uppercase tracking-[0.18em]">
                      <span>{entry.displayDate}</span>
                      <span aria-hidden="true">·</span>
                      <span>{entry.readTime}</span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>

        <ContactSection />
      </main>
    </>
  );
};

export default Transition(InsightsPage);
